<template>
  <div>
    <template v-if="!loading">
      <div
        v-if="model"
        style="margin-bottom: 2rem"
      >
        <h2 class="title is-5">
          <d-previous-btn
            :path="'/properties/' + $route.params.property + '/application-files'"
          />
          {{ $dayjs(model.lease_term_start_date).format('LL') }}
        </h2>
      </div>

      <d-container
        fluid
      >
        <div
          v-if="model"
          class="columns is-variable is-5 is-multiline"
        >
          <div class="column is-full-tablet is-full-mobile is-full-desktop">
            <div class="is-flex is-align-items-center mb-5">
              <div class="is-flex-grow-1 is-flex is-align-items-center">
                <h2 class="title is-5 mr-5 mb-0">
                  {{ $t("applicants") }}
                </h2>
              </div>
              <Button
                v-if="model.can.update"
                class="button is-primary is-small"
                type="button"
                @click="showCreateApplicantModal"
              >
                {{ $t("add_applicant") }}
              </Button>
            </div>
            <div>
              <d-card>
                <d-card-row>
                  <div class="table-container">
                    <table class="table  is-fullwidth fn-14">
                      <thead>
                        <tr>
                          <th />
                          <th>
                            {{ $t('step_modality') }}
                          </th>
                          <th>
                            {{ $t('identity') }}
                          </th>
                          <th>
                            {{ $t('finance') }}
                          </th>
                          <th>
                            {{ $t('credit') }}
                          </th>
                          <th>
                            {{ $t('application_file_completed_at') }}
                          </th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(applicant, key) in model.applicants"
                          :key="key"
                        >
                          <td>
                            <p class="mb-2 has-text-weight-semibold">
                              {{ applicant.first_name.capitalize() + ' ' + applicant.last_name.capitalize() }}
                            </p>
                            <p
                              v-if="applicant.applicant_type"
                              class="tag is-info is-light"
                            >
                              {{ applicant.applicant_type_label }}
                            </p>
                          </td>
                          <td>
                            <d-bool-icon
                              :true-value="true"
                              :false-value="false"
                              :value="applicant.has_accepted_terms_at ? true : false"
                            />
                          </td>
                          <td>
                            <d-bool-icon
                              :true-value="true"
                              :false-value="false"
                              :value="applicant.is_identity_step_completed ? true : false"
                            />
                          </td>
                          <td>
                            <d-bool-icon
                              :true-value="true"
                              :false-value="false"
                              :value="applicant.is_finance_step_completed ? true : false"
                            />
                          </td>

                          <td>
                            <d-bool-icon
                              :true-value="true"
                              :false-value="false"
                              :value="applicant.is_credit_step_completed ? true : false"
                            />
                          </td>
                          <td>
                            <span
                              v-if="applicant.completed_at"
                              class="ml-2 fn-13"
                            >
                              {{ $dayjs(applicant.completed_at).format('LL') }}
                            </span>
                            <span
                              v-else
                              class="ml-2 fn-13"
                            > - </span>
                          </td>
                          <td>
                            <div
                              class="is-flex is-align-items-center is-justify-content-flex-end"
                            >
                              <router-link
                                v-if="$roleCan([$laravel.role_admin])"
                                :to="{
                                  name: 'properties.show.application-files.show.applicants',
                                  params: {
                                    property: $route.params.property,
                                    applicationFile: model.id,
                                    applicant: applicant.id
                                  }
                                }"
                                class="button is-small is-white"
                                type="button"
                              >
                                <div class="icon has-text-grey">
                                  <i class="mdi mdi-eye mdi-18px" />
                                </div>
                              </router-link>

                              <a
                                v-if="$laravel && $laravel.user && $roleCan([$laravel.role_admin]) && applicant.is_completed"
                                :href="$laravel.route('api.application-files.applicants.credit_pdf', {
                                  applicationFile: model.id,
                                  applicant: applicant.id,
                                  property: model.property.id
                                })"
                                class="button is-small is-white"
                                target="_blank"
                              >
                                <div class="icon has-text-grey">
                                  <i class="mdi mdi-file-pdf-box mdi-18px" />
                                </div>

                              </a>

                              <a
                                v-if="$roleCan([$laravel.role_employee, $laravel.role_admin])"
                                :href="$laravel.route('api.applicants.files', {
                                  applicationFile: model.id,
                                  applicant: applicant.id,
                                  property: model.property.id
                                })"
                                class="button is-small is-white"
                                target="_blank"
                                :disabled="!applicant.applicant_files_count"
                              >
                                <div class="icon has-text-grey">
                                  <i class="mdi mdi-download mdi-18px" />
                                </div>
                              </a>

                              <Button
                                v-if="$roleCan([$laravel.role_admin])"
                                class="button is-small is-white"
                                type="button"
                                @click="confirmDeleteApplicant(applicant)"
                              >
                                <div class="icon has-text-grey">
                                  <i class="mdi mdi-delete mdi-18px" />
                                </div>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </d-card-row>
              </d-card>
            </div>
          </div>

          <div class="column is-full-tablet is-full-mobile is-full-desktop">
            <div class="is-flex is-align-items-center mb-5">
              <div class="is-flex-grow-1">
                <h2 class="title is-5 mr-5">
                  {{ $t("details").capitalize() }}
                </h2>
              </div>
              <router-link
                v-if="model.can.update"
                :to="{ path: '/properties/'+$route.params.property+'/application-files/'+model.id+'/edit' }"
                class="button is-link is-small is-underline"
                type="button"
              >
                {{ $t("edit") }}
              </router-link>
            </div>
            <v-description>
              <v-description-item>
                <template #left>
                  {{ $t('status').capitalize() }}
                </template>

                <template #right>
                  <p class="tag is-info is-light is-rounded">
                    {{ model.status_label }}
                  </p>
                </template>
              </v-description-item>
              <v-description-item>
                <template #left>
                  {{ $t('lease_term_start_date') }}
                </template>

                <template #right>
                  {{ $dayjs(model.lease_term_start_date).format('LL') }}
                </template>
              </v-description-item>
              <v-description-item>
                <template #left>
                  {{ $t('lease_term_end_date') }}
                </template>

                <template #right>
                  {{ $dayjs(model.lease_term_end_date).format('LL') }}
                </template>
              </v-description-item>
              <v-description-item>
                <template #left>
                  {{ $t('rent') }}
                </template>

                <template #right>
                  {{ model.rent }}$
                </template>
              </v-description-item>
              <v-description-item>
                <template #left>
                  {{ $t('heating_included') }}
                </template>

                <template #right>
                  <p>
                    {{
                      model.heating_included
                        ? $t("yes").capitalize()
                        : $t("no").capitalize()
                    }}
                  </p>
                </template>
              </v-description-item>

              <v-description-item>
                <template #left>
                  {{ $t('lighting_included') }}
                </template>

                <template #right>
                  <p>
                    {{
                      model.lighting_included
                        ? $t("yes").capitalize()
                        : $t("no").capitalize()
                    }}
                  </p>
                </template>
              </v-description-item>

              <v-description-item>
                <template #left>
                  {{ $t('inclusions').capitalize() }}
                </template>

                <template #right>
                  <div
                    v-if="model.property.active_inclusions"
                    class="content"
                  >
                    <ul class="mb-2">
                      <li
                        v-for="(inclusion, index) of model.property.active_inclusions"
                        :key="index"
                      >
                        <div>
                          <p class="mb-0">
                            {{ inclusion.inclusion_label.capitalize() }}
                          </p>
                        </div>
                      </li>
                    </ul>
                  </div>
                </template>
              </v-description-item>
            </v-description>
          </div>
        </div>
      </d-container>
      <b-modal
        v-if="model.can.update"
        v-model="isShownCreateApplicantModal"
        :width="600"
        @close="onCloseCreateApplicantModal"
      >
        <v-form
          ref="form"
          :data="form"
          :url="$laravel.route('api.applicants.store', {
            property: $route.params.property,
            application_file: $route.params.applicationFile
          })"
          method="post"
          @success="onSuccessCreateApplicant"
        >
          <template #default="{ errors }">
            <div class="modal-card">
              <header class="modal-card-head is-flex-direction-column is-flex is-align-items-flex-start is-size-6 has-text-weight-bold">
                {{ $t("add_applicant").capitalize() }}
              </header>
              <section class="modal-card-body">
                <div class="columns">
                  <div class="column">
                    <v-input
                      v-model="form.first_name"
                      required
                      name="first_name"
                      :label="$t('first_name')"
                    />
                  </div>
                  <div class="column">
                    <v-input
                      v-model="form.last_name"
                      required
                      name="last_name"
                      :label="$t('last_name')"
                    />
                  </div>
                </div>

                <div class="mb-5">
                  <v-input
                    v-model="form.email"
                    required
                    name="email"
                    :label="$t('email')"
                  />
                </div>
                <div class="mb-5">
                  <v-input-select
                    v-model="form.applicant_type"
                    name="applicant_type"
                    :label="$t('applicant_type')"
                    required
                  >
                    <option :value="null">
                      {{ $t("select").capitalize() }}
                    </option>
                    <option
                      v-for="(type, key) in $laravel.applicantTypesEnum"
                      :key="key"
                      :value="type.value"
                    >
                      {{ type.label }}
                    </option>
                  </v-input-select>
                </div>
                <div class="mb-2">
                  <v-input-select
                    v-model="form.locale"
                    name="locale"
                  >
                    <option :value="null">
                      {{ $t("select").capitalize() }}
                    </option>
                    <option
                      v-for="(locale, code) in $laravel.app.locales"
                      :key="code"
                      :value="code"
                    >
                      {{ locale }}
                    </option>
                  </v-input-select>
                </div>
              </section>
              <footer class="modal-card-foot">
                <Button
                  class="button is-light"
                  type="button"
                  @click="onCloseCreateApplicantModal"
                >
                  {{ $t("cancel") }}
                </Button>
                <Button
                  :label="$t('save')"
                  type="submit"
                  class=" button is-primary"
                >
                  {{ $t("save") }}
                </Button>
              </footer>
            </div>
          </template>
        </v-form>
      </b-modal>
    </template>
  </div>
</template>

<script>
import { i18n } from "@/services/i18n";

import BaseDescriptionList from "@/shared/components/BaseDescriptionList.vue";
import BaseDescriptionListItem from "@/shared/components/BaseDescriptionListItem.vue";

export default {
  components: {
    "v-description": BaseDescriptionList,
    "v-description-item": BaseDescriptionListItem,
  },
  metaInfo() {
    return {
      title: this.title,
      __application_file: this.title // for breadcrumb
    };
  },
  data () {
    return {
      model: {},
      loading: true,
      isShownCreateApplicantModal:false,
      form: {
        first_name: null,
        last_name: null,
        email: null,
        applicant_type: null,
        application_file_id: this.$route.params.applicationFile,
        locale: null,
      },
    };
  },
  computed: {
    title () {
      if (this.model) {
        return this.model.lease_term_start_date + " - " + this.model.lease_term_end_date;
      }
      return "";
    },
    actions(){
      if (window.Laravel.user.role !== "admin") {
        return [];
      }

      return [
        {
          label: i18n.t("edit"),
          path: "/application-files/" + this.model.id + "/edit",
          primary: true,
          color: "is-grey",
          icon: "pencil"
        }
      ];
    },
  },
  created () {
    this.query();
  },
  methods: {
    showCreateApplicantModal() {
      this.isShownCreateApplicantModal = true;
    },
    onSuccessCreateApplicant() {
      this.onCloseCreateApplicantModal();
      this.query();
    },
    onCloseCreateApplicantModal(){

      this.isShownCreateApplicantModal = false;

      this.form = {
        first_name: null,
        last_name: null,
        email: null,
        applicant_type: null,
        application_file_id: this.$route.params.applicationFile,
      };
    },
    confirmDeleteApplicant(applicant) {
      this.$buefy.dialog.confirm({
        title: this.$t("delete_applicant_title"),
        message: this.$t("delete_applicant_message"),
        cancelText: this.$t("cancel").capitalize(),
        confirmText: this.$t("delete").capitalize(),
        type: "is-danger",
        onConfirm: () => this.deleteApplicant(applicant.id),
      });
    },
    deleteApplicant (applicantId) {
      this.$http.delete(this.$laravel.route("api.applicants.destroy", {
        property: this.$route.params.property,
        application_file: this.$route.params.applicationFile,
        applicant: applicantId
      }))
        .then(response => {

          notificator.open({
            message: response.data.message,
            type: "success",
          });


          this.query();
        })
        .catch((e) => {
          console.error(e);
        });
    },
    query() {
      this.$http.get(this.$laravel.route("api.application-files.show", {
        property: this.$route.params.property,
        application_file: this.$route.params.applicationFile
      }))
        .then(response => {
          this.loading = false;
          this.model = response.data.data;
        })
        .catch((e) => {
          console.error(e);
          this.$router.push("/application-files");
        });
    }
  }
};
</script>
<style scoped>
  .is-size-8 {
    font-size: 13px;
  }
  .gap-5 {
    gap: 5px;
  }
  .fn-13 {
    font-size: 13px;
  }
  .fn-14 {
    font-size: 14px;
  }
  .table thead td, .table thead th {
    border-width: 0 0 1px !important;
  }
</style>
